import { useState, type FormEvent } from "react";
import { z } from "zod";
import { postDealerPortalV1DealerIdApplicationAppIdCoborrowerInvite } from "@porchpass/api-client";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useFormValidation,
  useApiCall,
  zodEmailRequired,
} from "@porchpass/ui";

const inviteSchema = z.object({
  email: zodEmailRequired(),
});

type InviteFormData = z.infer<typeof inviteSchema>;

interface InviteCoBorrowerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealerId: string;
  applicationId: string;
}

export default function InviteCoBorrowerDialog({
  open,
  onOpenChange,
  dealerId,
  applicationId,
}: InviteCoBorrowerDialogProps) {
  const [email, setEmail] = useState("");

  const { validate, fieldErrors, clearFieldError, clearAllErrors } =
    useFormValidation<InviteFormData>(inviteSchema);

  // --- API call ---

  const inviteApi = useApiCall(
    (body: { email: string }) => {
      if (!dealerId || !applicationId) {
        return Promise.reject(
          new Error("Dealer or application information is missing."),
        );
      }

      return postDealerPortalV1DealerIdApplicationAppIdCoborrowerInvite(
        dealerId,
        applicationId,
        body,
      );
    },
    {
      toast: {
        success: "Co-borrower invite sent",
        useServerErrorMessage: true,
      },
      onSuccess: () => {
        handleClose();
      },
    },
  );

  // --- Handlers (declared after useApiCall so they can reference inviteApi) ---

  const resetForm = () => {
    setEmail("");
    clearAllErrors();
    inviteApi.reset();
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  // --- Form submission ---

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate({ email });
    if (!result.success) return;
    void inviteApi.execute({ email: result.data.email });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[406px] rounded-[20px] p-0 gap-0">
        <DialogHeader className="border-b border-[#e4e7ec] px-6 py-4">
          <DialogTitle className="text-base font-semibold text-[#111827]">
            Invite co-borrower
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-6 space-y-4">
            <p className="text-sm text-[#6b7280]">
              Enter the co-borrower&apos;s email address to send them an
              invitation.
            </p>
            <Input
              label="Email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearFieldError("email");
              }}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
            />
          </div>
          <DialogFooter className="gap-4 justify-end px-6 pt-0 pb-6">
            <DialogClose asChild>
              <Button variant="ghost" size="s" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="primary"
              size="s"
              type="submit"
              loading={inviteApi.isLoading}
              disabled={inviteApi.isLoading}
            >
              Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
